'use client';

import { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';
import FilterBar from './FilterBar';
import ComparisonDrawer from './ComparisonDrawer';
import PackageConfigurator from './PackageConfigurator';
import CoverageChecker from './CoverageChecker';
import Icon from '@/components/ui/AppIcon';
import OrderModal from '@/components/OrderModal';

interface ServiceFeature {
  icon: string;
  text: string;
}

interface Service {
  id: string;
  title: string;
  description: string;
  price: string;
  priceUnit: string;
  features: ServiceFeature[];
  image: string;
  imageAlt: string;
  category:
    | 'wireless'
    | 'optical'
    | 'tv'
    | 'business'
    | 'surveillance'
    | 'msat'
    | 'webdev'
    | 'broadcasting'
    | 'routers';
  popular?: boolean;
  externalUrl?: string;
  ctaLabel?: string;
}

interface FilterOption {
  id: string;
  label: string;
  icon: string;
  count: number;
}

interface ConfigSectionOption {
  id: string;
  label: string;
  description: string;
  price: number;
  for?: 'wireless' | 'optical'; // <-- NOVO
}

interface ConfigSection {
  id: string;
  title: string;
  icon: string;
  options: ConfigSectionOption[];
}

interface CoverageResult {
  available: boolean;
  signal: 'excellent' | 'good' | 'fair' | 'poor';
  speed: string;
  technology: string;
}

const mockServices: Service[] = [
  // Wireless Internet Packages
  {
    id: 'wireless-1',
    title: 'Wireless Internet - Osnovni',
    description: 'Osnovni wireless internet paket idealan za pregledanje weba i email',
    price: '30.00',
    priceUnit: 'KM/mjesečno',
    features: [
      { icon: 'SignalIcon', text: '30 Mbps download / 6 Mbps upload' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShieldCheckIcon', text: 'Besplatna instalacija' },
      { icon: 'TvIcon', text: 'Dodajte TV za samo +10 KM' },
    ],
    image: '/photo-1670348654379-e736327ec4c0.jpg',
    imageAlt: 'Wireless internet ruter sa antenama na čistom radnom stolu',
    category: 'wireless',
  },
  {
    id: 'wireless-2',
    title: 'Wireless Internet - Biznis',
    description: 'Poslovni wireless internet sa pouzdanom brzinom za male firme',
    price: '35.00',
    priceUnit: 'KM/mjesečno',
    features: [
      { icon: 'SignalIcon', text: '35 Mbps download / 6 Mbps upload' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShieldCheckIcon', text: 'Prioritetna tehnička podrška' },
      { icon: 'TvIcon', text: 'Dodajte TV za samo +10 KM' },
    ],
    image: '/rocket_gen_img_14228d18f-1764664250133.png',
    imageAlt: 'Poslovni prostor sa wireless internet opremom',
    category: 'wireless',
    popular: true,
  },
  {
    id: 'wireless-3',
    title: 'Wireless Internet - Pro',
    description: 'Pro wireless internet za zahtjevne korisnike i srednje firme',
    price: '40.00',
    priceUnit: 'KM/mjesečno',
    features: [
      { icon: 'SignalIcon', text: '50 Mbps download / 6 Mbps upload' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShieldCheckIcon', text: '24/7 tehnička podrška' },
      { icon: 'TvIcon', text: 'Dodajte TV za samo +10 KM' },
    ],
    image: '/wireless-1.jpg',
    imageAlt: 'Profesionalni wireless internet setup sa visokim performansama',
    category: 'wireless',
  },

  // Optical Internet Packages
  {
    id: 'optical-1',
    title: 'Optički Internet - Osnovni',
    description: 'Osnovna optička konekcija sa stabilnom brzinom',
    price: '20.00',
    priceUnit: 'KM/mjesečno',
    features: [
      { icon: 'SignalIcon', text: '20 Mbps download / 2 Mbps upload' },
      { icon: 'WifiIcon', text: 'Besplatan WiFi ruter' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShieldCheckIcon', text: 'Besplatna instalacija' },
      { icon: 'TvIcon', text: 'Dodajte TV za samo +10 KM' },
    ],
    image: '/rocket_gen_img_1e1922b25-1764652218274.png',
    imageAlt: 'Optički internet kabel i ruter u modernom domu',
    category: 'optical',
  },
  {
    id: 'optical-2',
    title: 'Optički Internet - Biznis',
    description: 'Poslovni optički internet sa bržim upload brzinama',
    price: '30.00',
    priceUnit: 'KM/mjesečno',
    features: [
      { icon: 'SignalIcon', text: '40 Mbps download / 8 Mbps upload' },
      { icon: 'WifiIcon', text: 'Premium WiFi ruter' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShieldCheckIcon', text: 'Prioritetna tehnička podrška' },
      { icon: 'TvIcon', text: 'Dodajte TV za samo +10 KM' },
    ],
    image: '/rocket_gen_img_1911eef3f-1764687612546.png',
    imageAlt: 'Optička infrastruktura u poslovnom prostoru',
    category: 'optical',
    popular: true,
  },
  {
    id: 'optical-3',
    title: 'Optički Internet - Pro',
    description: 'Pro optički paket sa balansiranim brzinama za streaming i rad',
    price: '35.00',
    priceUnit: 'KM/mjesečno',
    features: [
      { icon: 'SignalIcon', text: '50 Mbps download / 20 Mbps upload' },
      { icon: 'WifiIcon', text: 'Premium WiFi 6 ruter' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShieldCheckIcon', text: '24/7 tehnička podrška' },
      { icon: 'TvIcon', text: 'Dodajte TV za samo +10 KM' },
    ],
    image: '/rocket_gen_img_1b91cdcca-1764664890953.png',
    imageAlt: 'Vrhunski optički internet setup za profesionalce',
    category: 'optical',
  },
  {
    id: 'optical-4',
    title: 'Optički Internet - Max',
    description: 'Maksimalna brzina za 4K streaming, gaming i velike transfere',
    price: '50.00',
    priceUnit: 'KM/mjesečno',
    features: [
      { icon: 'SignalIcon', text: '100 Mbps download / 50 Mbps upload' },
      { icon: 'WifiIcon', text: 'Premium WiFi 6 ruter' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShieldCheckIcon', text: 'Premium 24/7 podrška' },
      { icon: 'TvIcon', text: 'Dodajte TV za samo +10 KM' },
    ],
    image: '/rocket_gen_img_19a2cdaed-1764687609835.png',
    imageAlt: 'Najbrži optički internet sa naprednom opremom',
    category: 'optical',
    popular: true,
  },

  // TV Packages
  {
    id: 'tv-1',
    title: 'Kablovska Televizija',
    description: 'Osnovni TV paket sa 3 gratis TV uređaja',
    price: '10.00',
    priceUnit: 'KM/mjesečno uz internet',
    features: [
      { icon: 'TvIcon', text: '100+ TV kanala' },
      { icon: 'DevicePhoneMobileIcon', text: '3 TV prijemnika GRATIS' },
      { icon: 'SignalIcon', text: 'HD kvalitet slike' },
      { icon: 'ShieldCheckIcon', text: 'Besplatna instalacija' },
    ],
    image: '/photo-1717295248494-937c3a5655b1.jpg',
    imageAlt: 'Moderna TV sa kablovskim prijemnikom u dnevnoj sobi',
    category: 'tv',
  },
  {
    id: 'tv-2',
    title: 'IPTV',
    description: 'Napredni IPTV sistem sa fleksibilnim opcijama',
    price: '10.00',
    priceUnit: 'KM/mjesečno + 5 KM po TV Boxu',
    features: [
      { icon: 'TvIcon', text: '150+ TV kanala' },
      { icon: 'DevicePhoneMobileIcon', text: 'Prvi TV Box uključen' },
      { icon: 'PlayIcon', text: 'Dodatni TV Box: +5 KM' },
      { icon: 'SignalIcon', text: '4K Ultra HD podrška' },
      { icon: 'CloudIcon', text: 'Cloud DVR funkcija' },
    ],
    image: '/photo-1714646828046-9f555ebc9d62.jpg',
    imageAlt: 'IPTV TV Box sa daljinskim upravljačem na TV ormariću',
    category: 'tv',
    popular: true,
  },

  // Business Packages
  {
    id: 'povremeno-1',
    title: 'Wireless internet - povremeno',
    description: 'Kompletno rješenje za korisnike koji nemaju potrebu za stalnim korištenjem interneta',
    price: '50.00',
    priceUnit: 'KM/mjesec',
    features: [
      { icon: 'SignalIcon', text: 'Do 30 Mbps brzina' },
      { icon: 'ShieldCheckIcon', text: '24/7 tehnička podrška' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShoppingCartIcon', text: 'Oprema i usluge instalacije 350 KM' },
    ],
    image: '/blog-wireless-internet.jpg',
    imageAlt: '',
    category: 'business',
  },
  {
    id: 'povremeno-2',
    title: 'Optički internet - povremeno',
    description: 'Kompletno rješenje za korisnike koji nemaju potrebu za stalnim korištenjem interneta',
    price: '40.00',
    priceUnit: 'KM/mjesec',
    features: [
      { icon: 'SignalIcon', text: 'Do 50 Mbps brzina' },
      { icon: 'ShieldCheckIcon', text: '24/7 tehnička podrška' },
      { icon: 'ArrowPathIcon', text: 'Neograničen promet' },
      { icon: 'ShoppingCartIcon', text: 'Oprema i usluge instalacije 150 KM' },
    ],
    image: '/opticki-internet-ilustracija-tssrp1uja-ts-hero-v1_w967h544.jpg',
    imageAlt: '',
    category: 'business',
  },

  // Video Surveillance Services
  {
    id: 'surveillance-1',
    title: 'Video Nadzor - Osnovni Paket',
    description: 'Kompletno video nadzorno rješenje sa 4 kamere, rekorderom i instalacijom',
    price: '999.00',
    priceUnit: 'KM jednokratno',
    features: [
      { icon: 'VideoCameraIcon', text: '4 HD sigurnosne kamere' },
      { icon: 'ServerIcon', text: 'DVR/NVR rekorder uključen' },
      { icon: 'WrenchScrewdriverIcon', text: 'Kompletna profesionalna instalacija' },
      { icon: 'ShieldCheckIcon', text: '12 mjeseci garancije' },
      { icon: 'ClockIcon', text: '24/7 snimanje i čuvanje' },
    ],
    image: '/daua-4ch.jpg',
    imageAlt: 'Sigurnosne kamere montirane na zgradi sa profesionalnim sistemom video nadzora',
    category: 'surveillance',
    popular: true,
  },
  {
    id: 'surveillance-2',
    title: 'Video Nadzor - Prošireni Paket',
    description: 'Napredni video nadzor sa dodatnim kamerama i funkcijama',
    price: 'Po dogovoru',
    priceUnit: '',
    features: [
      { icon: 'VideoCameraIcon', text: 'Prilagođen broj kamera' },
      { icon: 'ServerIcon', text: 'Profesionalni NVR sistem' },
      { icon: 'DevicePhoneMobileIcon', text: 'Mobilni pristup i notifikacije' },
      { icon: 'WrenchScrewdriverIcon', text: 'Kompletna instalacija' },
      { icon: 'ShieldCheckIcon', text: 'Proširena garancija' },
    ],
    image: '/rocket_gen_img_1bc304115-1764771895816.png',
    imageAlt: 'Napredni sistem video nadzora sa višestrukim kamerama i centralnom kontrolom',
    category: 'surveillance',
  },

  // M-SAT TV Installation
  {
    id: 'msat-1',
    title: 'M-SAT TV Instalacija',
    description: 'Ovlašteni posrednik za M-SAT TV u Bosni i Hercegovini',
    price: 'Po cjenovniku',
    priceUnit: 'M-SAT',
    features: [
      { icon: 'TvIcon', text: 'Ovlaštena M-SAT instalacija' },
      { icon: 'SignalIcon', text: 'Profesionalno postavljanje antene' },
      { icon: 'WrenchScrewdriverIcon', text: 'Kompletna montaža i podešavanje' },
      { icon: 'ShieldCheckIcon', text: 'Garancija na instalaciju' },
      { icon: 'UserGroupIcon', text: 'Obuka za korištenje' },
    ],
    image: '/mSAT-tv.jpg',
    imageAlt: 'Satelitska antena za M-SAT TV montirana na krovu sa profesionalnom instalacijom',
    category: 'msat',
    popular: true,
  },
  {
    id: 'msat-2',
    title: 'M-SAT Premium Paket',
    description: 'Kompletno rješenje sa premium M-SAT paketima',
    price: 'Po dogovoru',
    priceUnit: '',
    features: [
      { icon: 'TvIcon', text: 'Premium M-SAT kanali' },
      { icon: 'SignalIcon', text: 'HD/4K kvalitet' },
      { icon: 'DevicePhoneMobileIcon', text: 'Dodatni prijemnici' },
      { icon: 'WrenchScrewdriverIcon', text: 'VIP instalacija' },
      { icon: 'PhoneIcon', text: 'Prioritetna tehnička podrška' },
    ],
    image: '/rocket_gen_img_19fc33d5b-1764771903060.png',
    imageAlt: 'Premium M-SAT TV setup sa višestrukim prijemnicima i naprednim funkcijama',
    category: 'msat',
  },

  // Website & Application Development Services
  {
    id: 'webdev-1',
    title: 'Web Stranica - Starter',
    description: 'Profesionalna web stranica sa modernim dizajnom i osnovnim funkcijama',
    price: '300.00',
    priceUnit: 'KM jednokratno',
    features: [
      { icon: 'ComputerDesktopIcon', text: 'Responzivni dizajn (mobilni i desktop)' },
      { icon: 'CodeBracketIcon', text: 'Do 5 stranica' },
      { icon: 'CloudIcon', text: 'Hosting i domena za 1 godinu' },
      { icon: 'WrenchScrewdriverIcon', text: 'Besplatna podrška 3 mjeseca' },
      { icon: 'RocketLaunchIcon', text: 'SEO optimizacija' },
    ],
    image: '/photo-1558174685-430919a96c8d.jpg',
    imageAlt: 'Laptop sa modernom web stranicom prikazanom na ekranu, uz šolju kafe na radnom stolu',
    category: 'webdev',
    popular: true,
  },
  {
    id: 'webdev-2',
    title: 'Web Aplikacija - Business',
    description: 'Kompleksna web aplikacija sa naprednim funkcionalnostima',
    price: 'Od 1000.00',
    priceUnit: 'KM (po dogovoru)',
    features: [
      { icon: 'ComputerDesktopIcon', text: 'Custom dizajn i funkcionalnosti' },
      { icon: 'DatabaseIcon', text: 'Baza podataka i backend' },
      { icon: 'ShieldCheckIcon', text: 'Sigurnost i autentifikacija korisnika' },
      { icon: 'CloudIcon', text: 'Cloud hosting i skalabilnost' },
      { icon: 'WrenchScrewdriverIcon', text: 'Održavanje i podrška 6 mjeseci' },
    ],
    image: '/rocket_gen_img_143a21815-1764651949320.png',
    imageAlt: 'Analitički dashboard web aplikacije sa grafovima i vizualizacijom podataka',
    category: 'webdev',
  },
  {
    id: 'webdev-3',
    title: 'Mobilna Aplikacija',
    description: 'Native ili cross-platform mobilna aplikacija za iOS i Android',
    price: 'Od 2000.00',
    priceUnit: 'KM (po dogovoru)',
    features: [
      { icon: 'DevicePhoneMobileIcon', text: 'iOS i Android platforme' },
      { icon: 'CodeBracketIcon', text: 'Native ili Flutter razvoj' },
      { icon: 'CloudIcon', text: 'Backend infrastruktura' },
      { icon: 'BellAlertIcon', text: 'Push notifikacije' },
      { icon: 'ShieldCheckIcon', text: '12 mjeseci održavanja' },
    ],
    image: '/rocket_gen_img_158750afa-1764657452819.png',
    imageAlt: 'Mobilni telefon sa modernom aplikacijom prikazanom na ekranu',
    category: 'webdev',
  },

  // Live Broadcasting Services
  {
    id: 'broadcast-1',
    title: 'Live Stream - Sportski Događaji',
    description: 'Profesionalni prenos uživo sportskih utakmica i događaja',
    price: 'Po dogovoru',
    priceUnit: '',
    features: [
      { icon: 'VideoCameraIcon', text: 'Multi-kamera setup za sportske događaje' },
      { icon: 'SignalIcon', text: 'Full HD ili 4K stream kvalitet' },
      { icon: 'GlobeAltIcon', text: 'Streaming na više platformi istovremeno' },
      { icon: 'UserGroupIcon', text: 'Podrška za veliki broj gledatelja' },
      { icon: 'ClockIcon', text: 'Snimanje i arhiviranje prenosa' },
    ],
    image: '/photo-1733570892887-f37aa0d63c5d.jpg',
    imageAlt: 'Profesionalna kamera za snimanje sportskog događaja na stadionu',
    category: 'broadcasting',
    popular: true,
  },
  {
    id: 'broadcast-2',
    title: 'Live Stream - Konferencije i Događaji',
    description: 'Stručan prenos poslovnih, kulturnih i društvenih događaja',
    price: 'Po dogovoru',
    priceUnit: '',
    features: [
      { icon: 'VideoCameraIcon', text: 'Profesionalna oprema i ekipa' },
      { icon: 'MicrophoneIcon', text: 'Audio setup i miksanje' },
      { icon: 'PresentationChartLineIcon', text: 'Integracija prezentacija i grafike' },
      { icon: 'ChatBubbleLeftRightIcon', text: 'Interakcija sa publikom (chat, Q&A)' },
      { icon: 'CloudIcon', text: 'Cloud recording i VOD' },
    ],
    image: '/rocket_gen_img_17a80e87c-1764706018049.png',
    imageAlt: 'Konferencijska sala sa profesionalnom streaming opremom i kamerama',
    category: 'broadcasting',
  },
  {
    id: 'broadcast-3',
    title: 'Live Stream - Kompletno Rješenje',
    description: 'All-in-one streaming platforma sa custom funkcionalnostima',
    price: 'Po dogovoru',
    priceUnit: '',
    features: [
      { icon: 'ServerIcon', text: 'Vlastita streaming platforma' },
      { icon: 'LockClosedIcon', text: 'Privatni streamovi ili pay-per-view' },
      { icon: 'ChartBarIcon', text: 'Analitika gledatelja u realnom vremenu' },
      { icon: 'WrenchScrewdriverIcon', text: 'Tehnička podrška tokom prenosa' },
      { icon: 'GlobeAltIcon', text: 'CDN distribucija za globalnu dostupnost' },
    ],
    image: '/photo-1535371166070-3797d6494fea.jpg',
    imageAlt: 'Kontrolna soba za live streaming sa više monitora i profesionalne opreme',
    category: 'broadcasting',
    popular: true,
  },

  // WiFi routeri (OLX / PIK — globalmediadoo)
  {
    id: 'routers-f3',
    title: 'Tenda F3 N300',
    description: 'Wireless N 300 Mbps ruter — pokrivenost do ~200 m², novo, popust na količinu',
    price: '45.00',
    priceUnit: 'KM',
    features: [
      { icon: 'WifiIcon', text: 'WiFi N 300 Mbps' },
      { icon: 'SignalIcon', text: 'Pokrivenost do ~200 m²' },
      { icon: 'ShieldCheckIcon', text: 'Novo — originalno pakovanje' },
      { icon: 'ShoppingCartIcon', text: 'Popust na količinu' },
    ],
    image: 'https://d4n0y8dshd77z.cloudfront.net/listings/79474912/sm/img-1789041390-0db610a5f993.jpg',
    imageAlt: 'Tenda F3 N300 wireless ruter',
    category: 'routers',
    externalUrl: 'https://olx.ba/artikal/79474912',
    ctaLabel: 'Pogledaj na OLX',
  },
  {
    id: 'routers-f6',
    title: 'Tenda F6 N300',
    description: 'Wireless N300 ruter sa 4 antene — novo, popust na količinu',
    price: '55.00',
    priceUnit: 'KM',
    features: [
      { icon: 'WifiIcon', text: 'WiFi N 300 Mbps' },
      { icon: 'SignalIcon', text: '4 vanjske antene' },
      { icon: 'ShieldCheckIcon', text: 'Novo — originalno pakovanje' },
      { icon: 'ShoppingCartIcon', text: 'Popust na količinu' },
    ],
    image: 'https://d4n0y8dshd77z.cloudfront.net/listings/79493809/sm/img-1789114375-5257a6a98fd6.jpg',
    imageAlt: 'Tenda F6 N300 wireless ruter',
    category: 'routers',
    externalUrl: 'https://olx.ba/artikal/79493809',
    ctaLabel: 'Pogledaj na OLX',
  },
  {
    id: 'routers-ac6',
    title: 'Tenda AC6 AC1200',
    description: 'Dual Band AC1200 WiFi ruter sa 4 antene — novo, popust na količinu',
    price: '70.00',
    priceUnit: 'KM',
    features: [
      { icon: 'WifiIcon', text: 'Dual Band AC1200' },
      { icon: 'SignalIcon', text: '4 vanjske antene' },
      { icon: 'ShieldCheckIcon', text: 'Novo — originalno pakovanje' },
      { icon: 'ShoppingCartIcon', text: 'Popust na količinu' },
    ],
    image: 'https://d4n0y8dshd77z.cloudfront.net/listings/79494228/sm/img-1789115267-0c810dc90c03.jpg',
    imageAlt: 'Tenda AC6 AC1200 Dual Band WiFi ruter',
    category: 'routers',
    popular: true,
    externalUrl: 'https://olx.ba/artikal/79494228',
    ctaLabel: 'Pogledaj na OLX',
  },
  {
    id: 'routers-ac11',
    title: 'Tenda AC11 AC1200',
    description: 'Dual-band gigabit WiFi ruter AC1200 — novo, popust na količinu',
    price: '80.00',
    priceUnit: 'KM',
    features: [
      { icon: 'WifiIcon', text: 'Dual Band AC1200 Gigabit' },
      { icon: 'SignalIcon', text: 'Više antena za jači signal' },
      { icon: 'ShieldCheckIcon', text: 'Novo — originalno pakovanje' },
      { icon: 'ShoppingCartIcon', text: 'Popust na količinu' },
    ],
    image: 'https://d4n0y8dshd77z.cloudfront.net/listings/79494551/sm/img-1789118791-66566197c74a.jpg',
    imageAlt: 'Tenda AC11 AC1200 dual-band gigabit WiFi ruter',
    category: 'routers',
    popular: true,
    externalUrl: 'https://olx.ba/artikal/79494551',
    ctaLabel: 'Pogledaj na OLX',
  },
];

const filterOptions: FilterOption[] = [
  { id: 'all', label: 'Svi paketi', icon: 'Squares2X2Icon', count: 23 },
  { id: 'wireless', label: 'Wireless Internet', icon: 'WifiIcon', count: 3 },
  { id: 'optical', label: 'Optički Internet', icon: 'SignalIcon', count: 4 },
  { id: 'tv', label: 'Televizija', icon: 'TvIcon', count: 2 },
  { id: 'business', label: 'Biznis', icon: 'BriefcaseIcon', count: 2 },
  { id: 'surveillance', label: 'Video Nadzor', icon: 'VideoCameraIcon', count: 2 },
  { id: 'msat', label: 'M-SAT TV', icon: 'SignalIcon', count: 2 },
  { id: 'webdev', label: 'Web & App Razvoj', icon: 'CodeBracketIcon', count: 3 },
  { id: 'broadcasting', label: 'Live Stream', icon: 'VideoCameraIcon', count: 3 },
  { id: 'routers', label: 'Ruteri', icon: 'WifiIcon', count: 4 },
];

const configuratorSections: ConfigSection[] = [
  {
    id: 'internet-type',
    title: 'Tip interneta',
    icon: 'SignalIcon',
    options: [
      { id: 'wireless', label: 'Wireless', description: 'Bežični internet', price: 30 },
      { id: 'optical', label: 'Optički', description: 'Optička konekcija', price: 20 },
    ],
  },
  {
    id: 'internet-speed',
    title: 'Brzina interneta',
    icon: 'SignalIcon',
    options: [
      // Wireless brzine
      { id: 'w-30-6', label: '30/6 Mbps', description: 'Wireless - osnovno', price: 0, for: 'wireless' },
      { id: 'w-35-6', label: '35/6 Mbps', description: 'Wireless - biznis', price: 5, for: 'wireless' },
      { id: 'w-50-6', label: '50/6 Mbps', description: 'Wireless - pro', price: 10, for: 'wireless' },

      // Optičke brzine
      { id: 'o-20-2', label: '20/2 Mbps', description: 'Optika - osnovno', price: 0, for: 'optical' },
      { id: 'o-40-8', label: '40/8 Mbps', description: 'Optika - biznis', price: 10, for: 'optical' },
      { id: 'o-50-20', label: '50/20 Mbps', description: 'Optika - pro', price: 15, for: 'optical' },
      { id: 'o-100-50', label: '100/50 Mbps', description: 'Optika - max', price: 30, for: 'optical' },
    ],
  },
  {
    id: 'tv-option',
    title: 'Televizija',
    icon: 'TvIcon',
    options: [
      { id: 'no-tv', label: 'Bez TV', description: 'Samo internet', price: 0 },
      { id: 'cable-tv', label: 'Kablovska TV', description: '+3 TV gratis', price: 10 },
      { id: 'iptv-1', label: 'IPTV - 1 Box', description: '1 TV Box uključen', price: 10 },
      { id: 'iptv-2', label: 'IPTV - 2 Boxa', description: '2 TV Boxa', price: 15 },
      { id: 'iptv-3', label: 'IPTV - 3 Boxa', description: '3 TV Boxa', price: 20 },
    ],
  },
];

export default function ServicesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [comparisonServices, setComparisonServices] = useState<string[]>([]);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderContext, setOrderContext] = useState<any>(null);

  // <-- NOVO: trenutno izabrani tip interneta (utiče na prikaz brzina)
  const [selectedInternetType, setSelectedInternetType] = useState<'wireless' | 'optical'>('wireless');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const filteredServices =
    activeFilter === 'all' ? mockServices : mockServices.filter((service) => service.category === activeFilter);

  const handleCompare = (serviceId: string) => {
    setComparisonServices((prev) => {
      if (prev.includes(serviceId)) return prev.filter((id) => id !== serviceId);
      if (prev.length >= 3) return prev;
      return [...prev, serviceId];
    });
  };

  const handleRemoveFromComparison = (serviceId: string) => {
    setComparisonServices((prev) => prev.filter((id) => id !== serviceId));
  };

  const handleClearComparison = () => {
    setComparisonServices([]);
  };

  const handleSelectService = (serviceId: string) => {
    setSelectedService(serviceId);

    const s = mockServices.find((x) => x.id === serviceId);
    if (!s) return;

    setOrderContext({
      source: 'service',
      serviceId: s.id,
      serviceTitle: s.title,
    });
    setOrderOpen(true);
  };
  
  const handleProceedOrder = (total: number, selections: Record<string, string>) => {
    setOrderContext({
      source: 'configurator',
      totalPrice: total,
      selections,
    });
    setOrderOpen(true);
  };


  const handleCoverageCheck = (address: string): CoverageResult => {
    const results: CoverageResult[] = [
      { available: true, signal: 'excellent', speed: 'Do 200 Mbps', technology: 'Fiber' },
      { available: true, signal: 'good', speed: 'Do 100 Mbps', technology: '4G LTE' },
      { available: true, signal: 'fair', speed: 'Do 50 Mbps', technology: '4G' },
      { available: false, signal: 'poor', speed: 'N/A', technology: 'N/A' },
    ];

    return results[Math.floor(Math.random() * results.length)];
  };

  // <-- NOVO: filtriraj sekciju "internet-speed" prema odabranom tipu
  const dynamicConfiguratorSections: ConfigSection[] = configuratorSections.map((section) => {
    if (section.id !== 'internet-speed') return section;

    return {
      ...section,
      options: section.options.filter((opt) => opt.for === selectedInternetType),
    };
  });

  const handleConfigCalculate = (total: number, selections: Record<string, string>) => {
    // Očekujemo da PackageConfigurator vraća selections['internet-type'] = 'wireless' | 'optical'
    const t = selections['internet-type'];
    if (t === 'wireless' || t === 'optical') {
      setSelectedInternetType(t);
    }

    console.log('Konfigurisani paket:', { total, selections });
  };

  const comparisonServicesData = mockServices.filter((service) => comparisonServices.includes(service.id));

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/services/components/ServicesInteractive.tsx:671:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="671" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-16%22%7D" className="min-h-screen bg-background pt-24 pb-16">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:672:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="672" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%22%7D" className="max-w-7xl mx-auto px-4">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:673:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="673" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:674:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="674" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-muted%20rounded-xl%22%7D" className="h-32 bg-muted rounded-xl" />
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:675:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="675" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20bg-muted%20rounded-xl%22%7D" className="h-16 bg-muted rounded-xl" />
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:676:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="676" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div data-component-id="src/app/services/components/ServicesInteractive.tsx:678:16" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="678" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" key={i} className="h-96 bg-muted rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/services/components/ServicesInteractive.tsx:688:4" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="688" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-32%22%7D" className="min-h-screen bg-background pt-24 pb-32">
      <div data-component-id="src/app/services/components/ServicesInteractive.tsx:689:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="689" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20space-y-8%22%7D" className="max-w-7xl mx-auto px-4 space-y-8">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:690:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="690" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20space-y-4%22%7D" className="text-center space-y-4">
          <h1 data-component-id="src/app/services/components/ServicesInteractive.tsx:691:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="691" data-component-file="ServicesInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-bold%20text-foreground%22%2C%22textContent%22%3A%22Na%C5%A1e%22%7D" className="text-4xl md:text-5xl font-bold text-foreground">
            Naše <span data-component-id="src/app/services/components/ServicesInteractive.tsx:692:17" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="692" data-component-file="ServicesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-primary%22%2C%22textContent%22%3A%22Usluge%22%7D" className="text-primary">Usluge</span>
          </h1>
          <p data-component-id="src/app/services/components/ServicesInteractive.tsx:694:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="694" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Prona%C4%91ite%20savr%C5%A1en%20paket%20za%20va%C5%A1e%20potrebe.%20Transparentne%20cijene%2C%20bez%20skrivenih%20tro%C5%A1kova%2C%20sa%20mogu%C4%87no%C5%A1%C4%87u%5Cn%20%20%20%20%20%20%20%20%20%20%20%20prilago%C4%91avanja.%22%7D" className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Pronađite savršen paket za vaše potrebe. Transparentne cijene, bez skrivenih troškova, sa mogućnošću
            prilagođavanja.
          </p>
        </div>

        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:700:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="700" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:701:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="701" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%22%7D" className="lg:col-span-2">
            <PackageConfigurator data-component-id="src/app/services/components/ServicesInteractive.tsx:702:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="702" data-component-file="ServicesInteractive.tsx" data-component-name="PackageConfigurator" data-component-content="%7B%22elementName%22%3A%22PackageConfigurator%22%7D"
              sections={dynamicConfiguratorSections}
              basePrice={0}
              onCalculate={handleConfigCalculate}
              onProceed={handleProceedOrder}
            />
          </div>
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:709:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="709" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <CoverageChecker data-component-id="src/app/services/components/ServicesInteractive.tsx:710:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="710" data-component-file="ServicesInteractive.tsx" data-component-name="CoverageChecker" data-component-content="%7B%22elementName%22%3A%22CoverageChecker%22%7D" onCheck={handleCoverageCheck} />
          </div>
        </div>

        <FilterBar data-component-id="src/app/services/components/ServicesInteractive.tsx:714:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="714" data-component-file="ServicesInteractive.tsx" data-component-name="FilterBar" data-component-content="%7B%22elementName%22%3A%22FilterBar%22%7D" activeFilter={activeFilter} onFilterChange={setActiveFilter} filters={filterOptions} />

        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:716:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="716" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) => (
            <ServiceCard data-component-id="src/app/services/components/ServicesInteractive.tsx:718:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="718" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
              key={service.id}
              {...service}
              onCompare={handleCompare}
              onSelect={handleSelectService}
              isComparing={comparisonServices.includes(service.id)}
            />
          ))}
        </div>

        {filteredServices.length === 0 && (
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:729:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="729" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:730:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="730" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20w-16%20h-16%20rounded-full%20bg-muted%20mb-4%22%7D" className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <Icon data-component-id="src/app/services/components/ServicesInteractive.tsx:731:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="731" data-component-file="ServicesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D" name="MagnifyingGlassIcon" size={32} className="text-muted-foreground" />
            </div>
            <h3 data-component-id="src/app/services/components/ServicesInteractive.tsx:733:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="733" data-component-file="ServicesInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Nema%20rezultata%22%7D" className="text-xl font-semibold text-foreground mb-2">Nema rezultata</h3>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:734:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="734" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Poku%C5%A1ajte%20sa%20drugom%20kategorijom%22%7D" className="text-muted-foreground">Pokušajte sa drugom kategorijom</p>
          </div>
        )}
      </div>

      <ComparisonDrawer data-component-id="src/app/services/components/ServicesInteractive.tsx:739:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="739" data-component-file="ServicesInteractive.tsx" data-component-name="ComparisonDrawer" data-component-content="%7B%22elementName%22%3A%22ComparisonDrawer%22%7D"
        services={comparisonServicesData}
        onRemove={handleRemoveFromComparison}
        onClear={handleClearComparison}
        isOpen={comparisonServices.length > 0}
      />
      <OrderModal data-component-id="src/app/services/components/ServicesInteractive.tsx:745:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="745" data-component-file="ServicesInteractive.tsx" data-component-name="OrderModal" data-component-content="%7B%22elementName%22%3A%22OrderModal%22%7D"
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        context={orderContext}
      />
    </div>
  );
}
